// Webhook dead letters (FR-70): deliveries that ran out of retries, across
// every company. A platform administrator can schedule one again.
import { Badge, Button, Loader, Stack, Table, Text } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";

type Delivery = Schemas["WebhookDelivery"];

const KEY = ["admin", "dead-letters"] as const;

export function AdminDeadLettersPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("admin_dead_letters.intro")}>
      <DeadLetters />
    </AdminLayout>
  );
}

function DeadLetters() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const deliveries = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/admin/webhook-deliveries", { params: { query: { limit: 200 } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = deliveries.data?.items ?? [];
  const eventLabel = (e: string) => t(`webhook_event.${e.replace(".", "_")}`, { defaultValue: e });

  const retry = async (d: Delivery) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/admin/webhook-deliveries/{delivery_id}/retry", {
      params: { path: { delivery_id: d.id } },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("admin_dead_letters.retried", { event: eventLabel(data.event_type) }));
    void queryClient.invalidateQueries({ queryKey: KEY });
  };

  return (
    <Stack gap="md">
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {deliveries.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : deliveries.isError ? (
        <FormError message={errorMessage(deliveries.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="dead-letters-empty">
          {t("admin_dead_letters.empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table striped verticalSpacing="sm" aria-label={t("admin.tab_dead_letters")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin_dead_letters.event")}</Table.Th>
                <Table.Th>{t("admin_dead_letters.endpoint")}</Table.Th>
                <Table.Th>{t("admin_dead_letters.attempts")}</Table.Th>
                <Table.Th>{t("admin_dead_letters.last_status")}</Table.Th>
                <Table.Th>{t("admin_dead_letters.dead_at")}</Table.Th>
                <Table.Th>{t("admin_companies.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((d) => (
                <Table.Tr key={d.id} data-testid="dead-letter-row">
                  <Table.Td>
                    <Text size="sm">{eventLabel(d.event_type)}</Text>
                    <Text size="xs" c="dimmed">
                      {f.dateTime(d.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {d.endpoint_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>{d.attempts}</Table.Td>
                  <Table.Td>
                    {d.last_status_code !== undefined ? (
                      <Badge color="red" variant="light">
                        {d.last_status_code}
                      </Badge>
                    ) : (
                      <Text size="sm">—</Text>
                    )}
                    {d.last_error && (
                      <Text size="xs" c="dimmed" style={{ wordBreak: "break-word" }}>
                        {d.last_error}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{d.dead_at ? f.dateTime(d.dead_at) : "—"}</Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => void retry(d)}
                      aria-label={t("admin_dead_letters.retry_for", {
                        event: eventLabel(d.event_type),
                        date: f.dateTime(d.created_at),
                      })}
                    >
                      {t("admin_dead_letters.retry")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
